import { RotateCcw } from "lucide-react";
import { PRODUCTS } from "@/content/products";
import { Placeholder } from "@/components/site/Placeholder";
import { EngageBand } from "@/components/site/EngageBand";
import { Container, Eyebrow, Mono, SectionTitle, StatusMarker } from "@/components/site/primitives";
import { ProductFoundation, ProductTop } from "@/components/products/ProductParts";

const P = PRODUCTS.briefcase;

const Hero = () => (
  <section data-testid="briefcase-hero">
    <Container className="py-14 lg:py-20">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="fade-up lg:col-span-7">
          <Eyebrow index={P.index}>Product · {P.activity}</Eyebrow>
          <h1 data-testid="page-title" className="mt-6 font-display text-5xl font-extrabold tracking-[-0.02em] sm:text-6xl">Briefcase</h1>
        </div>
        <div className="fade-up delay-1 lg:col-span-5">
          <p className="text-o-dim sm:text-lg">{P.tagline}</p>
          <div className="mt-6"><StatusMarker testid="status-briefcase" /></div>
        </div>
      </div>
      <Placeholder label={P.placeholder} note="Principal product imagery, wide format" ratio="21 / 9" className="fade-up delay-2 mt-12" testid="placeholder-briefcase-hero" />
    </Container>
  </section>
);

const Movements = () => (
  <section className="border-t border-o-line" data-testid="briefcase-movements">
    <Container className="grid gap-12 py-16 lg:grid-cols-12 lg:py-24">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-28">
          <Eyebrow>Organizing movements</Eyebrow>
          <SectionTitle className="mt-5 text-3xl sm:text-4xl lg:text-4xl">From leadership to consequential return.</SectionTitle>
          <p className="mt-4 text-o-dim">Briefcase is organized around four movements of shared work. The last returns to the first.</p>
        </div>
      </div>
      <ol className="space-y-4 lg:col-span-8">
        {P.movements.map((m, i) => (
          <li key={m.id} className="grid grid-cols-1 gap-6 border border-o-line bg-o-surface p-5 sm:grid-cols-12 sm:p-7" data-testid={`briefcase-movement-${m.id}`}>
            <div className="sm:col-span-5">
              <Mono accent>0{i + 1}</Mono>
              <h3 className="mt-3 font-display text-2xl font-bold">{m.label}</h3>
              <p className="mt-3 leading-relaxed text-o-dim">{m.text}</p>
            </div>
            <Placeholder label={`Briefcase · ${m.label.toLowerCase()}`} ratio="16 / 10" className="sm:col-span-7" />
          </li>
        ))}
        <li className="flex items-center gap-3 border border-dashed border-o-strong p-4">
          <RotateCcw className="h-4 w-4 shrink-0 text-o-accent" />
          <span className="text-sm text-o-dim">Consequential return informs the next direction.</span>
        </li>
      </ol>
    </Container>
  </section>
);

const Experience = () => (
  <section className="border-t border-o-line" data-testid="briefcase-experience">
    <Container className="space-y-12 py-16 lg:py-24">
      <Eyebrow>The product experience</Eyebrow>
      {[0, 1].map((i) => (
        <div key={i} className="grid gap-8 lg:grid-cols-12 lg:items-center">
          <Placeholder label={`Briefcase experience · view ${i + 1}`} ratio="3 / 2" className={i ? "lg:order-2 lg:col-span-7 lg:col-start-6" : "lg:col-span-7"} />
          <div className={i ? "lg:order-1 lg:col-span-4" : "lg:col-span-4 lg:col-start-9"}>
            <Mono accent>Experience explanation 0{i + 1}</Mono>
            <p className="mt-3 leading-relaxed text-o-dim">Space for an explanation of this part of the Briefcase experience, supplied in Phase 3 alongside the founder's imagery.</p>
          </div>
        </div>
      ))}
    </Container>
  </section>
);

export default function BriefcasePage() {
  return (
    <>
      <ProductTop id="briefcase" />
      <Hero />
      <Movements />
      <Experience />
      <ProductFoundation product={P} />
      <EngageBand path="research" from="/products/briefcase" title="Discuss Briefcase."
        text="Product collaboration and research inquiries. During Phase 1 this inquiry is an inactive preview." />
    </>
  );
}
